import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { BehaviorSubject, catchError, filter, forkJoin, map, Observable, of, race, ReplaySubject, switchMap, take, tap, throwError, timeout, timer } from 'rxjs';
import { GridService } from 'app/services/grid.service';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';

@Injectable({providedIn: 'root'})
export class NotificationsService
{
    _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    user: any; // Assicurati di avere l'ID dell'utente corrente
    allNotifications: any[] = [];
    unreadCount = 0;
    private _unreadCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private gridService: GridService,
        private authService: AuthService,
        private _userService: UserService
    )
    {
        this.user = this.authService.user;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    get unreadCount$(): Observable<number> {
        return this._unreadCount.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Recupera l'ID dell'utente corrente in modo sicuro da authService o localStorage
     */
    getCurrentUserId(): string {
        if (this.user?.id || this.user?._id) {
            return String(this.user.id || this.user._id);
        }

        try {
            const authUser: any = this.authService.user;
            if (authUser?.id || authUser?._id) {
                this.user = authUser;
                return String(authUser.id || authUser._id);
            }
        } catch (e) {
            // ignore
        }

        try {
            const raw = localStorage.getItem('user');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.id || parsed?._id) {
                    this.user = parsed;
                    return String(parsed.id || parsed._id);
                }
            }
        } catch (e) {
            // ignore
        }

        return '';
    }

    /**
     * Attende che l'utente sia presente (immediato se già disponibile, altrimenti aspetta UserService o storage)
     */
    waitForUserId(): Observable<string> {
        const immediateId = this.getCurrentUserId();
        if (immediateId) {
            return of(immediateId);
        }

        const fromUserService$ = this._userService.user$.pipe(
            map((u: any) => String(u?.id || u?._id || '')),
            filter((id: string) => !!id)
        );

        const fromStoragePolling$ = timer(100, 200).pipe(
            map(() => this.getCurrentUserId()),
            filter((id: string) => !!id)
        );

        return race(fromUserService$, fromStoragePolling$).pipe(
            take(1),
            tap(id => {
                if (id && (!this.user || !this.user.id)) {
                    this.user = { id, ...(this.user || {}) };
                }
            }),
            timeout({
                each: 5000,
                with: () => of('')
            }),
            catchError(() => of(''))
        );
    }

    /**
     * Get all notifications
     */
    getAll(): Observable<Notification[]>
    {
        return this._httpClient.get<Notification[]>('api/common/notifications').pipe(
            tap((notifications) =>
            {
                this._notifications.next([]);
            }),
        );
    }

    getNofications(): Observable<any> {
        return this.waitForUserId().pipe(
            switchMap((userId: string) => {
                if (!userId) {
                    console.warn('getNofications: Nessun utente disponibile al momento.');
                    return of([]);
                }

                const requestData = {
                    ViewCode: "NOTIFICATIONS.DEFAULT",
                    Filter: "{}"
                };

                return this.gridService.getScopeData("NOTIFICATIONS", "READ", requestData).pipe(
                    map((response: any) => {
                        const items = Array.isArray(response?.data) ? response.data : [];
                        // Filtra le notifiche per utente corrente e non eliminate
                        return items.filter(
                            (notification: any) => notification && notification.UserId === userId && !notification.Deleted
                        );
                    }),
                    tap((notifications: Notification[]) => {
                        if (notifications && notifications.length) {
                            notifications.forEach(notification => {
                                // Controlla se la notifica esiste già
                                const exists = this.allNotifications.some(existing => existing?._id === notification?._id);
                                if (!exists) {
                                    this.allNotifications.push(notification);
                                }
                            });
                            // Aggiorna il BehaviorSubject e ricalcola il conteggio
                            this._notifications.next(this.allNotifications);
                            this._calculateUnreadCount();
                        }
                    }),
                    catchError((error) => {
                        console.error('Errore durante il recupero delle notifiche:', error);
                        return of([]);
                    })
                );
            })
        );
    }
    

    _calculateUnreadCount(): void
    {
        const count = this.allNotifications.flat().filter(notification => !notification.Read).length;
        this._unreadCount.next(count);
        this.unreadCount = count;
    }

    /**
     * Create a notification
     *
     * @param notification
     
    create(notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<Notification>('api/common/notifications', {notification}).pipe(
                map((newNotification) =>
                {
                    // Update the notifications with the new notification
                    this._notifications.next([...notifications, newNotification]);

                    // Return the new notification from observable
                    return newNotification;
                }),
            )),
        );
    }*/


        addNotification(notification: Notification): void {
            this.allNotifications.push(notification);
            this._notifications.next(this.allNotifications);
            this.unreadCount = this.allNotifications.filter(n => !n.Read).length;
          }

    /**
     * Update the notification
     *
     * @param id
     * @param notification
     */
    update(_id: string, notification: Notification): Observable<any>
    {
        const requestData = {
            _id: _id, 
            Read: notification.Read,
            Title: notification.Title,
            Deleted: notification.Deleted,
            ReadDate: new Date().toISOString()  
        };
        return this.gridService.saveScopeData("NOTIFICATIONS", "UPSERT", requestData, false);
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean> {
        const updates = [];
        console.log("AllNotifications ",this.allNotifications);

        this.allNotifications = this.allNotifications.flat();

        this.allNotifications.forEach(notification => {
            if (!notification.Read) {
                notification.Read = true;

                updates.push(this.update(notification._id, notification));
            }
        });

        if (updates.length === 0) {
            return of(true); 
        }

        
        return forkJoin(updates).pipe(
            map(() => true), 
            catchError(error => {
                console.error('Errore durante l’aggiornamento delle notifiche:', error);
                return of(false);
            })
        );
    }

    deleteAll(): Observable<boolean> {
        const updates = [];
        console.log("AllNotifications ",this.allNotifications);

        this.allNotifications = this.allNotifications.flat();

        this.allNotifications.forEach(notification => {
            if (!notification.Deleted) {
                notification.Deleted = true;

                updates.push(this.update(notification._id, notification));
            }
        });

        if (updates.length === 0) {
            return of(true); 
        }

        
        return forkJoin(updates).pipe(
            map(() => true), 
            catchError(error => {
                console.error('Errore durante l’eliminazione delle notifiche:', error);
                return of(false);
            })
        );
    }

    
    
    
     
}
